import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../common/Loading';
import ReserveAssetModal from './ReserveAssetModal';
import { fetchAssetRequests } from '../../redux/assets/actions';

function AssetDetailsModal({ show, onClose, onReserve, asset, loadingHistory = false }) {
  const dispatch = useDispatch();
  const { assetRequests, assetRequestsLoaded, takeRequests } = useSelector((state) => state.assets);
  const { selectedUser } = useSelector((state) => state.users);
  const [showReserveModal, setShowReserveModal] = React.useState(false);
  const [isHoveringReserve, setIsHoveringReserve] = React.useState(false);

  React.useEffect(() => {
    if (!show) return;

    if (!selectedUser?.myportalUserId) return;

    if (!assetRequestsLoaded) {
      dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, takeRequests || 20));
    }
  }, [show, selectedUser, assetRequestsLoaded, dispatch, takeRequests]);

  const hasActiveRequestForAsset =
    !!asset &&
    Array.isArray(assetRequests) &&
    assetRequests.some((request) => request && request.status === 0 && request.inInventory && request.asset && request.asset.assetId === asset.assetId);

  if (!show || !asset) return null;

  const getStatusClass = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return 'badge badge-success';
      case 1:
        return 'badge badge-info';
      case 2:
        return 'badge badge-warning';
      case 3:
        return 'badge badge-danger';
      case 4:
        return 'badge badge-primary';
      default:
        return 'badge badge-secondary';
    }
  };

  const getStatusColor = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return '#28a745';
      case 1:
        return '#17a2b8';
      case 2:
        return '#ffc107';
      case 3:
        return '#dc3545';
      case 4:
        return '#007bff';
      default:
        return '#6c757d';
    }
  };

  const getStatusDisplayName = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return 'Available';
      case 1:
        return 'Spare';
      case 2:
        return 'In Repair';
      case 3:
        return 'Unavailable';
      case 4:
        return 'In Service';
      default:
        return 'Unknown';
    }
  };

  const formatDays = (days) => {
    if (!Number.isFinite(days)) return 'N/A';
    if (days >= 365) {
      const years = Math.round(days / 365);
      return `${years} year${years === 1 ? '' : 's'}`;
    }
    if (days >= 30) {
      const months = Math.round(days / 30);
      return `${months} month${months === 1 ? '' : 's'}`;
    }
    return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`;
  };

  const getAssetAgeText = () => {
    if (!asset.dateCreated) return 'N/A';
    const created = new Date(asset.dateCreated);
    const now = new Date();
    const diffDays = (now - created) / (1000 * 60 * 60 * 24);
    return formatDays(diffDays);
  };

  const getLoanDurationStats = () => {
    const history = asset.history || [];
    const durations = history
      .filter((h) => h.loanDate && h.returnDate)
      .map((h) => {
        const start = new Date(h.loanDate);
        const end = new Date(h.returnDate);
        return (end - start) / (1000 * 60 * 60 * 24);
      })
      .filter((d) => Number.isFinite(d) && d >= 0);

    if (durations.length === 0) {
      return { avg: 'N/A', longest: 'N/A' };
    }

    const sum = durations.reduce((a, b) => a + b, 0);
    const avgDays = sum / durations.length;
    const longestDays = Math.max(...durations);
    return { avg: formatDays(avgDays), longest: formatDays(longestDays) };
  };

  const { avg: averageLoanText, longest: longestLoanText } = getLoanDurationStats();
  const totalLoanCount = asset.historyTotalCount ?? (asset.history ? asset.history.length : 0);

  const canReserveByStatus = asset.assetStatus === 0 || asset.assetStatus === 1;
  const isReserveDisabled = !canReserveByStatus || hasActiveRequestForAsset;

  const handleReserveClick = () => {
    setShowReserveModal(true);
  };

  const handleReserveSubmit = (reservationData) => {
    if (typeof onReserve === 'function') {
      onReserve(reservationData);
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <>
      {!showReserveModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}>
          <div className="modal-dialog modal-lg" style={{ width: '90%', maxWidth: '900px' }}>
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="modal-title mb-0">{asset.name}</h5>
                </div>
                <div className="d-flex align-items-center">
                  <span
                    className="badge"
                    style={{
                      border: `2px solid ${getStatusColor(asset.assetStatus)}`,
                      color: getStatusColor(asset.assetStatus),
                      backgroundColor: 'transparent',
                      padding: '8px 16px',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                    }}>
                    {getStatusDisplayName(asset.assetStatus)}
                  </span>
                </div>
              </div>

              <div className="modal-body" style={{ minHeight: '350px' }}>
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Creation Date
                      </div>
                      <div>{new Date(asset.dateCreated).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Release Date
                      </div>
                      <div>{asset.releaseDate ? new Date(asset.releaseDate).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Asset Age
                      </div>
                      <div>{getAssetAgeText()}</div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-8">
                    <h6 className="mb-3 fw-bold" style={{ fontWeight: 'bold' }}>
                      Asset History
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th className="fw-light">Loan Date</th>
                            <th className="fw-light">Return Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingHistory ? (
                            <tr>
                              <td colSpan="2" className="text-center" style={{ paddingTop: '20px' }}>
                                <Loading message="Loading assets..." />
                              </td>
                            </tr>
                          ) : asset.history && asset.history.length > 0 ? (
                            asset.history.map((entry, index) => (
                              <tr key={index}>
                                <td>{new Date(entry.loanDate).toLocaleDateString()}</td>
                                <td>{entry.returnDate ? new Date(entry.returnDate).toLocaleDateString() : 'N/A'}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="2" className="text-center text-muted" style={{ paddingTop: '20px' }}>
                                No history available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <h6 className="mb-3 fw-bold" style={{ fontWeight: 'bold' }}>
                      Category
                    </h6>
                    <div className="mb-4">{asset.assetCategory?.name || 'N/A'}</div>
                    <h6 className="mb-3 fw-bold" style={{ fontWeight: 'bold' }}>
                      Tenant
                    </h6>
                    <div>{asset.tenant?.name || 'N/A'}</div>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Times Loaned
                      </div>
                      <div>{totalLoanCount}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Average Loan Duration
                      </div>
                      <div>{averageLoanText}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-2">
                      <div className="fw-bold" style={{ fontWeight: 'bold' }}>
                        Longest Loan Duration
                      </div>
                      <div>{longestLoanText}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ position: 'relative' }}>
                <button type="button" className="btn btn-secondary" onClick={onClose} style={{ width: '150px' }}>
                  CLOSE
                </button>
                <span
                  onMouseEnter={() => hasActiveRequestForAsset && setIsHoveringReserve(true)}
                  onMouseLeave={() => setIsHoveringReserve(false)}
                  style={{ display: 'inline-block', position: 'relative' }}>
                  <button
                    type="button"
                    className={`btn ${canReserveByStatus ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={handleReserveClick}
                    disabled={isReserveDisabled}
                    style={{ width: '150px', cursor: isReserveDisabled ? 'not-allowed' : 'pointer' }}>
                    RESERVE
                  </button>
                  {hasActiveRequestForAsset && isHoveringReserve && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '110%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#333',
                        color: '#fff',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                      }}>
                      You already have an active request for this asset. Please wait until it has been processed.
                    </div>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <ReserveAssetModal show={showReserveModal} onClose={() => setShowReserveModal(false)} onReserve={handleReserveSubmit} asset={asset} />
    </>
  );
}

export default AssetDetailsModal;
